import React, { useState } from 'react';
import {
  CheckCircle2,
  Zap,
  Shield,
  HelpCircle,
  Sparkles,
  ArrowRight,
  Clock,
  Gauge,
  CreditCard,
  Lock,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { User, Subscription, isProSubscriber } from '../../shared/types';
import { api } from '../lib/api';

export interface PricingPageProps {
  user: User | null;
  subscription: Subscription | null;
  onOpenAuthModal: () => void;
  onNavigate: (path: string) => void;
}

export function PricingPage({
  user,
  subscription,
  onOpenAuthModal,
  onNavigate,
}: PricingPageProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPro = isProSubscriber(subscription);

  const handleStartTrial = async () => {
    setError(null);
    if (!user) {
      onOpenAuthModal();
      return;
    }

    try {
      setIsLoading(true);
      const res = await api.billing.createCheckoutSession();
      if (res.url) {
        window.location.href = res.url;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start Stripe checkout session. Please try again.');
      setIsLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setError(null);
    try {
      setIsLoading(true);
      const res = await api.billing.createPortalSession();
      if (res.url) {
        window.location.href = res.url;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to open customer portal.');
      setIsLoading(false);
    }
  };

  return (
    <div className="container max-w-5xl py-12 md:py-16 space-y-16">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3.5 py-1 text-xs font-bold text-amber-600 dark:text-amber-400">
          <Sparkles className="h-3.5 w-3.5" />
          Risk-Free Assessment Preparation
        </div>

        <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-foreground dark:text-white leading-tight">
          Invest in Your Career with{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-600 dark:from-amber-400 dark:via-amber-300 dark:to-yellow-500">
            MaintenancePrep Pro
          </span>
        </h1>

        <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
          Start with a <strong className="text-foreground dark:text-white">14-day free trial</strong>. Experience unlimited realistic 60-minute mock simulations, schematic diagrams, and verified solutions. Cancel anytime.
        </p>
      </div>

      {error && (
        <div className="max-w-md mx-auto p-4 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive text-sm text-center">
          {error}
        </div>
      )}

      {/* Pricing Card */}
      <div className="max-w-lg mx-auto">
        <div className="relative rounded-3xl border-2 border-amber-500/40 bg-gradient-to-b from-card to-background p-8 md:p-10 shadow-2xl shadow-amber-500/10 overflow-hidden">
          {/* Top highlight ribbon */}
          <div className="absolute top-0 right-0 transform translate-x-8 translate-y-4 rotate-45 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-extrabold text-[10px] uppercase py-1 px-10 shadow-md">
            14-Day Free Trial
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2">
                <Badge variant="warning" className="text-xs px-2.5 py-0.5 font-bold uppercase tracking-wider">
                  Full Access Pass
                </Badge>
              </div>
              <h2 className="text-2xl font-bold text-foreground dark:text-white mt-3">
                Monthly Pro Membership
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Everything you need to score 70%+ and pass your maintenance assessment.
              </p>
            </div>

            {/* Price block */}
            <div className="rounded-2xl bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 p-5 space-y-1.5">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl md:text-5xl font-extrabold text-foreground dark:text-white font-mono">
                  $0.00
                </span>
                <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                  for 14 days
                </span>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <span>then only</span>
                <strong className="text-foreground dark:text-white text-sm font-mono">$9.99 / month</strong>
                <span>• Cancel anytime with 1 click</span>
              </div>
            </div>

            {/* Feature List */}
            <div className="space-y-3.5 pt-2">
              <div className="text-xs font-bold text-foreground dark:text-slate-200 uppercase tracking-wider">
                What's included:
              </div>

              {[
                'Unlimited 60-Minute Real Timed Mock Exams',
                'All 4 Tracks: Mechanical, Electrical, Multicraft & Aptitude',
                'High-Resolution Circuit Schematics & Hydraulic P&ID Diagrams',
                'Step-by-step Formulas, Calculations & Logic Proofs',
                'Candidate Diagnostic Readiness Scoring & Pass Benchmark',
                'Sync with iOS & Android Apps (Launching Q4 2026)',
                'Safe & encrypted Stripe checkout with 14-day free trial',
              ].map((feat, i) => (
                <div key={i} className="flex items-start gap-3 text-xs md:text-sm text-foreground dark:text-slate-300">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>

            {/* Action CTA Button */}
            <div className="pt-4 space-y-3">
              {isPro ? (
                <div className="space-y-2">
                  <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-xs text-center font-medium">
                    ✅ You currently have an active Pro Membership ({subscription?.status === 'trialing' ? '14-Day Free Trial' : 'Active'})
                  </div>
                  <Button
                    variant="amber"
                    size="lg"
                    onClick={handleManageSubscription}
                    disabled={isLoading}
                    className="w-full text-base font-bold shadow-lg shadow-amber-500/20"
                  >
                    {isLoading ? 'Opening Stripe Portal...' : 'Manage Subscription & Invoices'}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="amber"
                  size="lg"
                  onClick={handleStartTrial}
                  disabled={isLoading}
                  className="w-full text-base font-bold shadow-xl shadow-amber-500/25 gap-2"
                >
                  <Zap className="h-4 w-4 fill-current" />
                  {isLoading ? 'Connecting to Stripe...' : 'Start 14-Day Free Trial'}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}

              <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground pt-1">
                <span className="flex items-center gap-1">
                  <Lock className="h-3 w-3 text-muted-foreground" />
                  SSL Encrypted
                </span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <CreditCard className="h-3 w-3 text-muted-foreground" />
                  Powered by Stripe
                </span>
                <span>•</span>
                <span>Cancel Anytime</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Frequently Asked Questions */}
      <div className="max-w-3xl mx-auto pt-8 border-t border-border space-y-8">
        <div className="text-center">
          <h2 className="text-xl md:text-2xl font-bold text-foreground dark:text-white">
            Frequently Asked Questions
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Common questions about the free trial and subscription.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="p-5 rounded-2xl border border-border bg-card/60 dark:bg-slate-900/40 space-y-2">
            <h3 className="font-bold text-sm text-foreground dark:text-white flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" aria-hidden="true" />
              How does the 14-day free trial work?
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              You get instant full access to every mock exam, question bank, and schematic diagram immediately for 14 days. You won't be charged anything today ($0.00). If you decide to keep it, it continues at $9.99/month.
            </p>
          </div>

          <div className="p-5 rounded-2xl border border-border bg-card/60 dark:bg-slate-900/40 space-y-2">
            <h3 className="font-bold text-sm text-foreground dark:text-white flex items-center gap-2">
              <Shield className="h-4 w-4 text-emerald-500" aria-hidden="true" />
              Can I cancel before the trial ends?
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Yes, absolutely! You can cancel at any time during your 14 days with one click from your candidate dashboard via Stripe, and you will not be charged a single penny.
            </p>
          </div>

          <div className="p-5 rounded-2xl border border-border bg-card/60 dark:bg-slate-900/40 space-y-2">
            <h3 className="font-bold text-sm text-foreground dark:text-white flex items-center gap-2">
              <Gauge className="h-4 w-4 text-sky-500" aria-hidden="true" />
              Which assessment tracks are included?
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              All four: Mechanical Maintenance, Electrical & Electronics, Multicraft Maintenance, and Mechanical Aptitude.
            </p>
          </div>

          <div className="p-5 rounded-2xl border border-border bg-card/60 dark:bg-slate-900/40 space-y-2">
            <h3 className="font-bold text-sm text-foreground dark:text-white flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-purple-500" aria-hidden="true" />
              What payment methods does Stripe accept?
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Stripe accepts all major credit and debit cards (Visa, Mastercard, American Express, Discover), as well as Apple Pay and Google Pay.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
